import { existsSync, readFileSync } from 'node:fs';

/**
 * ENG-5901 (ADR-0018 Phase 1) — fail-fast probe for `${VAR}` substitution
 * gaps at MCP spawn time.
 *
 * `.mcp.json` carries `${VAR}` placeholders that Claude Code substitutes
 * from the spawn environment (ADR-0006). Two failure modes:
 *
 *   - var **unset** with no `:-default`: Claude Code refuses to parse the
 *     config at startup (loud, per the Claude Code MCP docs). Still worth
 *     a structured line so the operator's first grep explains claude's
 *     parse error.
 *   - var **set but empty** (`FOO=` line in `.env.integrations`, or an
 *     empty export): substitution "succeeds" with `""`, the MCP boots,
 *     the upstream API 401s, and the channel dies silently. This is the
 *     case the probe exists for.
 *
 * The probe is **observational only** — it emits one structured stderr
 * line per gap and never blocks the spawn. Greppable signature
 * (documented in docs/operator/credential-migration-eng5898.md):
 *
 *   [mcp-env-substitution] missing var=<NAME> server=<KEY> state=<unset|empty>
 *
 * Pure helpers + a thin fs wrapper; unit-testable without a spawn.
 */

/**
 * Vars that are legitimately absent/empty at probe time because a later
 * layer binds them (or deliberately leaves them unset):
 *
 *   - AGT_RUN_ID: exported per-spawn by the manager for scheduled runs
 *     (ENG-4561) and intentionally unset for sessions with no `runs` row
 *     (the augmented bridge maps missing → null run id; ENG-5818).
 *   - AGT_TOKEN: exchanged at runtime by the broker (missing makes it
 *     fall back to AGT_API_KEY → /host/exchange); never in the spawn env
 *     by design. Flagged as noise on agt-aws-1 (stirling/xero).
 *   - ANCHOR_BROWSER_SESSION_ID: seeded EMPTY into .env.integrations by
 *     remoteMcp envDefaults (ENG-5855) and minted per-session later
 *     (ENG-5857) — empty at spawn is the designed state, not a failure.
 */
export const LATE_BOUND_VARS: ReadonlySet<string> = new Set([
  'AGT_RUN_ID',
  'AGT_TOKEN',
  'ANCHOR_BROWSER_SESSION_ID',
]);

export interface MissingSubstitutionVar {
  varName: string;
  /** Server key in `mcpServers` whose entry references the var. */
  server: string;
  state: 'unset' | 'empty';
}

/**
 * `${VAR}` with no default. `${VAR:-default}` can't fail substitution, so
 * the probe ignores it (same rule the Claude Code docs imply: only a
 * defaultless reference to an unset var is fatal).
 */
const TEMPLATE_VAR_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

function collectVarsFromValue(value: unknown, into: Set<string>): void {
  if (typeof value === 'string') {
    for (const m of value.matchAll(TEMPLATE_VAR_RE)) into.add(m[1]!);
  } else if (Array.isArray(value)) {
    for (const v of value) collectVarsFromValue(v, into);
  }
}

/**
 * Find every defaultless `${VAR}` referenced by each MCP server entry
 * (env values, headers values, url, command, args) whose value in `env`
 * is unset or empty/whitespace. Returns one finding per (server, var).
 */
export function findMissingSubstitutionVars(
  mcpConfig: unknown,
  env: Record<string, string | undefined>,
): MissingSubstitutionVar[] {
  const findings: MissingSubstitutionVar[] = [];
  if (typeof mcpConfig !== 'object' || mcpConfig === null) return findings;
  const servers = (mcpConfig as { mcpServers?: Record<string, unknown> }).mcpServers;
  if (typeof servers !== 'object' || servers === null) return findings;

  for (const [server, raw] of Object.entries(servers)) {
    if (typeof raw !== 'object' || raw === null) continue;
    const entry = raw as Record<string, unknown>;
    const vars = new Set<string>();
    collectVarsFromValue(entry['command'], vars);
    collectVarsFromValue(entry['args'], vars);
    collectVarsFromValue(entry['url'], vars);
    for (const block of [entry['env'], entry['headers']]) {
      if (typeof block !== 'object' || block === null) continue;
      for (const v of Object.values(block)) collectVarsFromValue(v, vars);
    }
    for (const varName of vars) {
      if (LATE_BOUND_VARS.has(varName)) continue;
      const value = env[varName];
      if (value === undefined) {
        findings.push({ varName, server, state: 'unset' });
      } else if (value.trim() === '') {
        findings.push({ varName, server, state: 'empty' });
      }
    }
  }
  return findings;
}

/** The structured, secret-free stderr line. */
export function formatMissingVar(f: MissingSubstitutionVar): string {
  return `[mcp-env-substitution] missing var=${f.varName} server=${f.server} state=${f.state}`;
}

/**
 * ENG-6232 — substitute `${VAR}` placeholders in a single string against `env`,
 * mirroring how Claude Code expands the spawn environment into `.mcp.json`
 * values (ADR-0006). The host-side connectivity probe needs this because it
 * runs in the **manager** process, which never carries the per-agent integration
 * tokens — those live only in the agent's `.env.integrations` (overlaid into the
 * probe env) and in the agent's Claude child at spawn. Without substitution the
 * probe sends the literal `Authorization: Bearer ${GRANOLA_ACCESS_TOKEN}`, earns
 * a guaranteed 401, and reports a false `down` while the agent itself (which DOES
 * expand the var) works fine.
 *
 * Only the defaultless `${VAR}` form is substituted (same rule as
 * {@link findMissingSubstitutionVars}). A var that is unset, empty, or a
 * {@link LATE_BOUND_VARS} member is reported in `unresolved` and left as the
 * literal `${VAR}` — so callers can detect it and **skip** the probe rather than
 * fire a doomed request that would read as a false `down`.
 */
export function expandTemplateVars(
  value: string,
  env: Record<string, string | undefined>,
): { value: string; unresolved: string[] } {
  const unresolved = new Set<string>();
  const expanded = value.replace(TEMPLATE_VAR_RE, (literal, name: string) => {
    if (LATE_BOUND_VARS.has(name)) {
      unresolved.add(name);
      return literal;
    }
    const resolved = env[name];
    if (resolved !== undefined && resolved.trim() !== '') return resolved;
    unresolved.add(name);
    return literal;
  });
  return { value: expanded, unresolved: [...unresolved] };
}

/**
 * Parse a `.env.integrations` body with the same semantics as the
 * scheduled-task loader in manager-worker (skip blanks/comments, split on
 * first `=`) plus shell-quote stripping: the writer shell-quotes values
 * (`shellQuote`) because the persistent path `source`s the file, so a
 * Node-side reader must undo `'...'` wrapping to see the real value.
 */
export function parseEnvIntegrations(content: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of content.split('\n')) {
    if (!line || line.startsWith('#') || !line.includes('=')) continue;
    const eqIdx = line.indexOf('=');
    const key = line.slice(0, eqIdx);
    let value = line.slice(eqIdx + 1);
    if (value.length >= 2 && value.startsWith("'") && value.endsWith("'")) {
      // shellQuote wraps in single quotes and escapes embedded ones as
      // `'\''` — reverse both so the probe sees what the shell would.
      value = value.slice(1, -1).replaceAll("'\\''", "'");
    }
    out[key] = value;
  }
  return out;
}

/**
 * Convenience wrapper for spawn sites: read the rendered `.mcp.json` and
 * (optionally) `.env.integrations`, overlay the env file onto `baseEnv`
 * (mirroring what the wrapper's `source` / the scheduled-task loader
 * does), and return the findings. Never throws — a probe must not be
 * able to break a spawn.
 */
export function probeMcpEnvSubstitution(args: {
  mcpConfigPath: string;
  envIntegrationsPath?: string;
  baseEnv: Record<string, string | undefined>;
}): MissingSubstitutionVar[] {
  try {
    const config = JSON.parse(readFileSync(args.mcpConfigPath, 'utf-8'));
    let env = args.baseEnv;
    if (args.envIntegrationsPath && existsSync(args.envIntegrationsPath)) {
      env = {
        ...args.baseEnv,
        ...parseEnvIntegrations(readFileSync(args.envIntegrationsPath, 'utf-8')),
      };
    }
    return findMissingSubstitutionVars(config, env);
  } catch {
    return [];
  }
}
